import React from "react";
import { logger } from "../../utility/logger";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Top-level error boundary. Without one, a render error in any lazily-loaded
 * screen would unmount the whole app under the single <Suspense> boundary and
 * leave a blank page. This catches the error, logs it for diagnostics, and shows
 * a recoverable fallback instead.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Diagnostics only — do not surface internal detail in the UI.
    logger.error("Unhandled UI error:", error, info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div role="alert" style={{ padding: "2rem", textAlign: "center" }}>
            <h2>Something went wrong.</h2>
            <p>
              Please reload the page. If the problem persists, contact support.
            </p>
            <button type="button" onClick={this.handleReload}>
              Reload
            </button>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
